import { Group, Stack } from '@mantine/core';
import * as React from 'react';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StepFieldsRemoveButton from '@/pages/Editor/components/StepFieldsRemoveButton.tsx';
import OutputPinButton from '@/pages/Editor/components/StepSidebar/OutputPinButton.tsx';
import SingleChildDataInputSelector from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleChildDataInputSelector.tsx';
import { ErrorBoundaryOutputAlias, FailureHandlerStep } from '@/types/automationSteps.ts';
import { StepType } from '@/types/automationFields.ts';
import { DEFAULT_DYNAMIC_DATA_INPUT, STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';

// Error-boundary aliases intentionally support only direct leaf steps in
// their selected protected or recovery scope. The backend enforces the same
// contract; filtering here prevents configurations that cannot be saved.
const ERROR_BOUNDARY_NESTED_SCOPE_STEP_TYPES = new Set<StepType>([
  StepType.Iterate,
  StepType.Repeat,
  StepType.Retry,
  StepType.FailureHandler,
  StepType.Conditional,
]);

type Props = {
  alias: ErrorBoundaryOutputAlias;
  label: string;
  onChangeDelay: (alias: ErrorBoundaryOutputAlias) => void;
  onChangeNoDelay: (alias: ErrorBoundaryOutputAlias) => void;
  onDelete: () => void;
  pinnable?: boolean;
  sourceScope?: 'protected' | 'recovery';
  step: FailureHandlerStep;
};

export function FailureHandlerOutputAliasFields({
  alias,
  label,
  onChangeDelay,
  onChangeNoDelay,
  onDelete,
  pinnable = false,
  sourceScope = 'protected',
  step,
}: Props) {
  const eligibleChildIds = (sourceScope === 'protected' ? step.children : step.handler_children)
    .filter((child) => !ERROR_BOUNDARY_NESTED_SCOPE_STEP_TYPES.has(child.step_type))
    .map((child) => child.step_id);

  return (
    <Stack style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }} bg="#F6F6F6" p="xs" gap="xs">
      <Group gap="xs" wrap="nowrap" align="start">
        <StyledTextInput
          label="Name"
          value={alias.name}
          placeholder={label}
          onChange={(event) => onChangeDelay({ ...alias, name: event.currentTarget.value.replace(/[/\n]/g, '') })}
          required
          maxLength={STEP_FIELD_MAX_LENGTH}
          w="100%"
        />
        {pinnable && <OutputPinButton step={step} outputId={alias.output_id} />}
        <StepFieldsRemoveButton onDelete={onDelete} />
      </Group>
      <SingleChildDataInputSelector
        step={step}
        label={sourceScope === 'protected' ? 'Original child output' : 'Response child output'}
        dataInput={alias.child_data_output}
        onChange={(dataInput) =>
          onChangeNoDelay({ ...alias, child_data_output: dataInput || DEFAULT_DYNAMIC_DATA_INPUT })
        }
        required
        allowedStepIds={eligibleChildIds}
      />
    </Stack>
  );
}
